import {TicketStatusCategory} from '@frontapp/ticketing';
import React, {ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {ticketStatusCategoryColors} from '../../../../../../libs/inboxes/ui/src/ticketing/ticketStatus';
import {Localizer} from '../../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {
  defineMessage,
  Plural,
  Trans,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {isChannelType} from '../../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../../libs/shared/core/src/models/api/recipientModel';
import {TicketingPreferencesModel} from '../../../../../../libs/shared/core/src/models/api/ticketingPreferencesModel';
import {ResourceMap} from '../../../../../../libs/shared/core/src/store/commonAppTypes';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {Skeleton} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {InteractiveDateTimeFormatter} from '../../../../../components/common/formatters/dateTime/interactiveDateTimeFormatter';
import {RandomSkeleton} from '../../../../../components/common/randomSkeleton';
import {ActivityConversationStageUpdatePopover} from '../../../../../components/inboxes/viewer/activity/activityConversationStageUpdate/activityConversationStageUpdatePopover';
import {ActivityRulePillContainer} from '../../../../../components/inboxes/viewer/activity/activityRulePill/activityRulePillContainer';
import {ActivitySourceTypesEnum} from '../../../../../models/api/activitySourceModel';
import {
  AnalyticsTimelineEvent,
  AnalyticsTimelineEventsEnum,
  AnalyticsTimelineEventSource,
  filterMessageReceivers,
} from '../../../../../models/api/analyticsTimelineEventModel';
import {CardModel} from '../../../../../models/api/cardModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {EllipsisTooltipText} from '../../../../../settings/ellipsisTooltipText';
import {ConversationCardTag} from '../conversations/conversationCardTag';
import {TimelineMessagePreview} from './timelineMessagePreview';

/*
 * Styles.
 */

const StyledEventDiv = styled.div`
  grid-area: event;
  overflow: hidden;
`;

const StyledShowHiddenEventsContainerDiv = styled.div`
  grid-area: button;
  width: fit-content;
`;

const StyledEventDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.medium};
  ${ellipsis()}
`;

const StyledEventDescriptionWrapperDiv = styled.div`
  display: flex;
`;

interface StyledIconCircleDivProps {
  $backgroundStyle?: 'inverted' | 'fill';
}

const StyledIconCircleDiv = styled.div<StyledIconCircleDivProps>`
  grid-area: icon;

  height: 24px;
  width: 24px;
  ${(p) => createBackground(p.$backgroundStyle)};
  border-radius: 50%;

  ${flexCenter};
  justify-content: center;
`;

function createBackground(background?: StyledIconCircleDivProps['$backgroundStyle']) {
  if (!background || background === 'fill') {
    return css`
      background-color: ${(p) => p.theme.greys.shade30};
    `;
  }
  return css`
    border: 1px solid ${(p) => p.theme.greys.shade30};
    box-sizing: border-box;
  `;
}

const StyledHiddenEventsCircleDiv = styled.div`
  grid-area: icon;

  height: 8px;
  width: 8px;
  margin: 8px;
  background-color: ${(p) => p.theme.greys.shade40};
  border-radius: 50%;

  ${flexCenter};
  justify-content: center;
`;

const StyledEventTeammateAndDateDiv = styled.div`
  display: flex;
  margin-top: 4px;
  margin-bottom: 2px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

const StyledEventTeammateDiv = styled.div`
  ${ellipsis()}
`;

const StyledEventBodyDiv = styled.div`
  grid-area: body;
  overflow: hidden;
  padding-bottom: 8px;
`;

const StyledBoldEventDescriptionDiv = styled.div`
  font-weight: ${fontWeights.bold};
  margin-right: 4px;
`;

const StyledEventDateDiv = styled.div`
  flex-shrink: 0;
`;

const StyledSkeletonIcon = styled(Skeleton)`
  grid-area: icon;
`;

const StyledRandomSkeletonWrapperDiv = styled.div`
  grid-area: event;
`;

/*
 * Props.
 */

interface RenderEventProps {
  eventIcon: IconName;
  eventIconColorRenderer?: (theme: UserTheme) => string | undefined;
  backgroundStyle?: StyledIconCircleDivProps['$backgroundStyle'];
  eventDescription: ReactNode;
  eventBody?: ReactNode;
  eventDate: number;
  eventSource?: AnalyticsTimelineEventSource;
  tag?: TagModel;
}

/*
 * Constants.
 */

export const timelineLoadingItem = (
  <>
    <StyledSkeletonIcon width="24px" height="24px" borderRadius="50%" />
    <StyledRandomSkeletonWrapperDiv>
      <RandomSkeleton height="24px" />
    </StyledRandomSkeletonWrapperDiv>
  </>
);

/*
 * Helpers.
 */

function renderEventDescription(eventDescription: ReactNode, tag?: TagModel) {
  return (
    <StyledEventDescriptionWrapperDiv>
      {eventDescription}
      {tag && <ConversationCardTag tag={tag} />}
    </StyledEventDescriptionWrapperDiv>
  );
}

function renderEventSourceAndDate(eventDate: number, eventSource?: AnalyticsTimelineEventSource) {
  return (
    <StyledEventTeammateAndDateDiv>
      {eventSource && renderEventSource(eventSource)}
      <StyledEventDateDiv>
        <InteractiveDateTimeFormatter relativeFormat={RelativeDateTimeFormatsEnum.SHORT} value={eventDate} />
      </StyledEventDateDiv>
    </StyledEventTeammateAndDateDiv>
  );
}

function renderEventSource(eventSource: AnalyticsTimelineEventSource) {
  if (eventSource.ruleId && eventSource.label) {
    return (
      <>
        <ActivityRulePillContainer
          ruleName={eventSource.label}
          ruleId={eventSource.ruleId}
          ruleBundleId={eventSource.ruleBundleId}
          ruleBundleType={eventSource.ruleBundleType}
          ruleNamespace={eventSource.ruleNamespace}
        />
        <div>&nbsp;{'\u2022'}&nbsp;</div>
      </>
    );
  }
  return (
    <>
      <StyledEventTeammateDiv>{eventSource.label}</StyledEventTeammateDiv>
      <div>&nbsp;{'\u2022'}&nbsp;</div>
    </>
  );
}

function maybeRenderEventBody(eventBody: ReactNode) {
  if (!eventBody) {
    return null;
  }

  return <StyledEventBodyDiv>{eventBody}</StyledEventBodyDiv>;
}

function renderEvent({
  eventIcon,
  eventIconColorRenderer,
  backgroundStyle,
  eventSource,
  eventDescription,
  eventBody,
  eventDate,
  tag,
}: RenderEventProps) {
  return (
    <>
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconCircleDiv $backgroundStyle={backgroundStyle}>
            <Icon name={eventIcon} size={14} color={eventIconColorRenderer?.(theme) ?? theme.greys.shade70} />
          </StyledIconCircleDiv>
        )}
      </UserThemeConsumer>
      <StyledEventDiv>
        {renderEventDescription(eventDescription, tag)}
        {renderEventSourceAndDate(eventDate, eventSource)}
      </StyledEventDiv>
      {maybeRenderEventBody(eventBody)}
    </>
  );
}

/*
 * Renders a single timeline event.
 * NOTE: We pass down a full list of messages and cards here because the `timelineEvent` only has references to the resource UIDs.
 * Therefore, we do a reverse lookup in order to render the necessary resources correctly for `inbound`, `outbound`, and `out_reply` events.
 */
export function renderTimelineEvent(
  timelineEvent: AnalyticsTimelineEvent,
  messagesResourcesMap: ResourceMap<MessageModel>,
  cardsResourcesMap: ResourceMap<CardModel>,
  userId: number,
  ticketingPreferences: TicketingPreferencesModel | undefined,
  localize: Localizer,
) {
  switch (timelineEvent.type) {
    case AnalyticsTimelineEventsEnum.ARCHIVE:
      return renderEvent({
        eventIcon: 'archiveActivityFilled',
        eventSource: timelineEvent.archivedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-archived">
            <StyledBoldEventDescriptionDiv>Archived</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>
              in {timelineEvent.archivedIn || 'a deleted inbox'}
            </StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.ASSIGN:
      return renderEvent({
        eventIcon: 'assignActivityFilled',
        eventSource: timelineEvent.assignedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-assigned">
            <StyledBoldEventDescriptionDiv>Assigned</StyledBoldEventDescriptionDiv>
            {timelineEvent.assignedTo && (
              <StyledEventDescriptionDiv>to {timelineEvent.assignedTo}</StyledEventDescriptionDiv>
            )}
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.UNASSIGN:
      return renderEvent({
        eventIcon: 'unassignActivityFilled',
        eventSource: timelineEvent.unassignedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-unassigned">
            <StyledBoldEventDescriptionDiv>Unassigned</StyledBoldEventDescriptionDiv>
            {timelineEvent.unassignedFrom && (
              <StyledEventDescriptionDiv>from {timelineEvent.unassignedFrom}</StyledEventDescriptionDiv>
            )}
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.INBOUND: {
      const {message, fromCard, fallbackSenderHandle} = retrieveFullMessageData(
        timelineEvent.messageUid,
        messagesResourcesMap,
        cardsResourcesMap,
      );

      if (!message) {
        return null;
      } // This shouldn't happen.

      return renderEvent({
        eventIcon: 'inboxInboundFilled',
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-inbound">
            <StyledBoldEventDescriptionDiv>Received</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>
              message from {fromCard?.name ?? fallbackSenderHandle}
            </StyledEventDescriptionDiv>
          </Trans>
        ),
        eventBody: (
          <TimelineMessagePreview
            message={message}
            from={fromCard}
            fallbackSenderHandle={fallbackSenderHandle}
            activityId={timelineEvent.id}
          />
        ),
        eventDate: timelineEvent.date,
      });
    }
    case AnalyticsTimelineEventsEnum.OUTBOUND: {
      const {message, fromCard, fallbackSenderHandle, sentTo} = retrieveFullMessageData(
        timelineEvent.messageUid,
        messagesResourcesMap,
        cardsResourcesMap,
        true,
      );

      if (!message) {
        return null;
      } // This shouldn't happen.

      return renderEvent({
        eventIcon: 'sendFilled',
        eventSource: {label: fromCard?.name ?? fallbackSenderHandle},
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-outbound">
            <StyledBoldEventDescriptionDiv>Message</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>sent to {sentTo}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventBody: (
          <TimelineMessagePreview
            message={message}
            from={fromCard}
            fallbackSenderHandle={fallbackSenderHandle}
            activityId={timelineEvent.id}
          />
        ),
        eventDate: timelineEvent.date,
      });
    }
    case AnalyticsTimelineEventsEnum.OUT_REPLY: {
      const {message, fromCard, fallbackSenderHandle, sentTo} = retrieveFullMessageData(
        timelineEvent.messageUid,
        messagesResourcesMap,
        cardsResourcesMap,
        true,
      );

      if (!message) {
        return null;
      } // This shouldn't happen.

      return renderEvent({
        eventIcon: 'reply',
        eventSource: {label: fromCard?.name ?? fallbackSenderHandle},
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-out-reply">
            <StyledBoldEventDescriptionDiv>Reply</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>sent to {sentTo}</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventBody: (
          <TimelineMessagePreview
            message={message}
            from={fromCard}
            fallbackSenderHandle={fallbackSenderHandle}
            activityId={timelineEvent.id}
          />
        ),
        eventDate: timelineEvent.date,
      });
    }
    case AnalyticsTimelineEventsEnum.CONV_JOIN:
      return renderEvent({
        eventIcon: 'mergeActivityFilled',
        eventSource: timelineEvent.convJoinedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-conv-joined">
            <StyledBoldEventDescriptionDiv>Merged</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>the conversation</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.CONV_SPLIT:
      return renderEvent({
        eventIcon: 'splitActivityFilled',
        eventSource: timelineEvent.convSplittedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-conv-splitted">
            <StyledBoldEventDescriptionDiv>Split</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>the conversation</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.MOVE:
      return renderEvent({
        eventIcon: 'moveActivityFilled',
        eventSource: timelineEvent.movedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-moved">
            <StyledBoldEventDescriptionDiv>Moved</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>
              to {timelineEvent.movedIn || 'a deleted inbox'}
            </StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.REMINDER:
      return renderEvent({
        eventIcon: 'snoozeActivityFilled',
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-reminder">
            <StyledBoldEventDescriptionDiv>Snoozed</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>expired</StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.REOPEN:
      return renderEvent({
        eventIcon: 'unarchiveActivityFilled',
        eventSource: timelineEvent.reopenedBy,
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-reopened">
            <StyledBoldEventDescriptionDiv>Reopened</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>
              in {timelineEvent.reopenedIn || 'a deleted inbox'}
            </StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.SNOOZE:
      // TODO: Fetch snooze expiration date using reminder_id.
      return renderEvent({
        eventIcon: 'snoozeActivityFilled',
        eventSource: timelineEvent.snoozedBy,
        eventDescription: (
          <StyledBoldEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-snoozed">Snoozed</Trans>
          </StyledBoldEventDescriptionDiv>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.TAG:
      return renderEvent({
        eventIcon: 'tagActivityFilled',
        eventSource: timelineEvent.taggedBy,
        eventDescription: (
          <StyledBoldEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-tag">Added</Trans>
          </StyledBoldEventDescriptionDiv>
        ),
        tag: timelineEvent.tag,
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.UNTAG:
      return renderEvent({
        eventIcon: 'untagActivityFilled',
        eventSource: timelineEvent.untaggedBy,
        tag: timelineEvent.tag,
        eventDescription: (
          <StyledBoldEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-untagged">Removed</Trans>
          </StyledBoldEventDescriptionDiv>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.CONVERSATION_STAGE_UPDATE:
      return renderEvent({
        eventIcon: 'gearFilled',
        eventDescription: (
          <Trans id="analytics-side-panel-conversation-timeline-stage-update">
            <StyledBoldEventDescriptionDiv>Marked</StyledBoldEventDescriptionDiv>
            <StyledEventDescriptionDiv>
              as{' '}
              <ActivityConversationStageUpdatePopover
                conversationStageType={timelineEvent.conversationStageType}
                size={VisualSizesEnum.SMALL}
              />
            </StyledEventDescriptionDiv>
          </Trans>
        ),
        eventDate: timelineEvent.date,
      });

    case AnalyticsTimelineEventsEnum.TICKET_STATUS_UPDATE: {
      const ticketStatus = ticketingPreferences?.statuses.find(
        (status) => status.tagId === timelineEvent.ticketStatusTag.id,
      );

      const isOpenTicketStatus = ticketStatus?.category === TicketStatusCategory.OPEN;

      let eventSource;

      if (timelineEvent.source === ActivitySourceTypesEnum.TEAMMATE && timelineEvent.from) {
        eventSource = {label: userId === timelineEvent.from.id ? 'you' : timelineEvent.from.display_name};
      }

      if (isOpenTicketStatus && isChannelType(timelineEvent.source)) {
        eventSource = {
          label: localize(
            defineMessage({
              id: 'activity-basic-ticket-status-by-inbound',
              message: 'by this reply',
            }),
          ),
        };
      }

      return renderEvent({
        eventIcon: 'squareFilled',
        eventIconColorRenderer: (theme) =>
          ticketStatus
            ? theme.palette[ticketStatusCategoryColors[ticketStatus?.category]].shade40
            : undefined,
        backgroundStyle: 'inverted',
        eventSource,
        eventDescription: (
          <StyledEventDescriptionDiv>
            <Trans id="analytics-side-panel-conversation-timeline-ticket-update">
              <EllipsisTooltipText overflowHidden>
                <strong>{timelineEvent.ticketStatusTag.name}</strong> in{' '}
                {renderInboxName(timelineEvent.inboxes)}
              </EllipsisTooltipText>
            </Trans>
          </StyledEventDescriptionDiv>
        ),
        eventDate: timelineEvent.date,
      });
    }

    default:
      throw new UnreachableError(timelineEvent);
  }
}

export function renderHiddenEventsButton(hiddenEventsCount: number, onClick: () => void) {
  return (
    <>
      <StyledHiddenEventsCircleDiv />
      <StyledShowHiddenEventsContainerDiv>
        <Button variant={ButtonVariantsEnum.LINK} size={VisualSizesEnum.SMALL} onClick={onClick}>
          {`${hiddenEventsCount} `}
          <Plural
            id="analytics-timeline-show-hidden-events"
            value={hiddenEventsCount}
            one="event hidden"
            other="events hidden"
          />
        </Button>
      </StyledShowHiddenEventsContainerDiv>
    </>
  );
}

/*
 * Helpers specific to the inbound, outbound and out_reply events.
 */

// Get the Card of the message sender. We return the first card that has a matching contactUid.
function retrieveSenderCardFromMessage(message: MessageModel, cardsResourcesMap: ResourceMap<CardModel>) {
  const fromRecipient = message?.recipients.find((r) => r.role === RecipientRolesEnum.FROM); // Finding the FROM recipient.

  if (!fromRecipient?.contactUid) {
    return undefined;
  }

  const fromCard = cardsResourcesMap?.[fromRecipient.contactUid]; // Finding the correct card.

  return fromCard;
}

// From a messageUid will return the full Message and the sender's Card.
// For outbound and out_reply messages we also compute the "sentTo" string.
function retrieveFullMessageData(
  messageUid: string,
  messagesResourcesMap: ResourceMap<MessageModel>,
  cardsResourcesMap: ResourceMap<CardModel>,
  shouldProcessSentTo?: boolean,
) {
  const message = messagesResourcesMap[messageUid];
  const fromCard = message && retrieveSenderCardFromMessage(message, cardsResourcesMap);

  const fallbackSenderHandle =
    message?.recipients.find((r) => r.role === RecipientRolesEnum.FROM)?.handle || '';

  const sentTo =
    message &&
    shouldProcessSentTo &&
    filterMessageReceivers(message.recipients)
      .map((recipient) => recipient.name ?? recipient.handle)
      .join(', ');

  return {message, fromCard, sentTo, fallbackSenderHandle};
}

function renderInboxName(inboxes: ReadonlyArray<{id: number; name: string}>) {
  if (!inboxes.length) {
    return undefined;
  }

  if (inboxes.length === 1) {
    return inboxes[0].name;
  }

  return <Trans id="activity-basic-inbox-multiple">{inboxes.length} inboxes</Trans>;
}
